import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorScreen } from './ErrorScreen';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Default UI for `ErrorBoundary`. Lives here rather than inside the class so it
 * can call `useTranslation`, which a class component cannot.
 */
function ErrorBoundaryFallback({ error, onReset }: { error: Error | null; onReset: () => void }) {
  const { t } = useTranslation('errors');

  return (
    <ErrorScreen
      is404={false}
      title={t('boundary.somethingWentWrong')}
      message={t('boundary.unexpectedError')}
    >
      <button type="button" onClick={onReset} className="btn-primary">
        {t('tryAgain')}
      </button>
      <button
        type="button"
        onClick={() => {
          window.location.href = '/';
        }}
        className="btn-secondary"
      >
        {t('boundary.goHome')}
      </button>
      {import.meta.env.DEV && error && (
        <details className="mt-4 w-full text-left">
          <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
            {t('boundary.errorDetails')}
          </summary>
          <pre className="mt-2 overflow-auto rounded-lg bg-gray-100 p-4 text-xs">
            {error.message}
            {'\n\n'}
            {error.stack}
          </pre>
        </details>
      )}
    </ErrorScreen>
  );
}

/**
 * Catches render-time errors below it and shows a recoverable screen instead of
 * a blank page. Complements `RouterErrorBoundary`, which only sees errors the
 * router itself raises (no match, loader throws).
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // A stale chunk after a deploy is not a real error -- the old hashed asset
    // is simply gone. Reload once to pick up the new manifest; the sessionStorage
    // flag stops it becoming a reload loop when the chunk is genuinely broken.
    const isChunkError =
      error.name === 'ChunkLoadError' ||
      error.message?.includes('Failed to fetch dynamically imported module') ||
      error.message?.includes('Loading chunk') ||
      error.message?.includes('Importing a module script failed') ||
      error.message?.includes('error loading dynamically imported module');

    if (isChunkError) {
      try {
        const key = 'chunk-error-reload';
        if (!sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, '1');
          window.location.reload();
          return;
        }
        sessionStorage.removeItem(key);
      } catch {
        // sessionStorage unavailable -- fall through to the error UI.
      }
    }

    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleReset = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // `!== undefined`, because `null` is a MEANINGFUL fallback: "render nothing". Treating
      // it as falsy fell through to the full-screen error card, which is 60vh with its own
      // heading and buttons — wedged into the header's flex row on every route, for a caller
      // that had asked for the opposite.
      if (this.props.fallback !== undefined) return this.props.fallback;
      return <ErrorBoundaryFallback error={this.state.error} onReset={this.handleReset} />;
    }

    return this.props.children;
  }
}
